import {StyleSheet, css} from 'aphrodite';
import React, {useState} from 'react';

import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {checkIsAnalysisArchived, showFlowchartEditFunctionality} from 'analysis-tool/EditAnalysisHelpers';
import {CanEditReturnValue} from 'analysis-tool/EditAnalysisHelpers';
import {checkCanModifyStep} from 'analysis-tool/EditStepHelpers';
import {
  AnalysisFlowchartAction,
  AnalysisFlowchartAvailableAction,
} from 'analysis-tool/flowchart/actions/AnalysisFlowchartActionTypes';
import AnalysisFlowchart from 'analysis-tool/flowchart/AnalysisFlowchart';
import {AnalysisFlowchartMode} from 'analysis-tool/flowchart/AnalysisFlowchartContext';
import refreshAnalysis from 'analysis-tool/refreshAnalysis';
import EditStepRightSidebar from 'analysis-tool/right-sidebar/components/EditStepRightSidebar';
import {RightSidebarWidth} from 'analysis-tool/right-sidebar/components/RightSidebarSkeleton';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {STEP_TYPE_TO_USER_FACING_NAME} from 'graphql-client/__generated/GeneratedEnumMaps';
import {stepIsConfigEditorSupported} from 'pipelines/generalized-step-config-editor/stepTypeIsConfigEditorSupported';

function getAvailableActions(
  setStepIdToEdit: (stepId: string) => void,
  analysis: AnalysisGraphQL
): Array<AnalysisFlowchartAvailableAction> {
  if (!showFlowchartEditFunctionality(analysis)) {
    return [];
  }

  const actions: Array<AnalysisFlowchartAvailableAction> = [
    {
      action: AnalysisFlowchartAction.OPEN_STEP_CONFIGURATION,
      onAction: ({stepId}: {stepId: string}) => {
        setStepIdToEdit(stepId);
      },
    },
  ];

  return BenchlingConfig.ENABLE_ANALYSIS_TOOL_INTERACTIVE_FLOWCHART_EXPERIMENTAL
    ? [
        ...actions,
        {
          action: AnalysisFlowchartAction.ADD_STEP as AnalysisFlowchartAction.ADD_STEP,
          onAction: ({stepId}: {stepId: string}) => {
            setStepIdToEdit(stepId);
          },
        },
        {action: AnalysisFlowchartAction.DELETE_STEP},
        {action: AnalysisFlowchartAction.DELETE_STEP_AND_CHILDREN},
        {action: AnalysisFlowchartAction.RERUN_STEP},
      ]
    : actions;
}

type AnalysisFlowchartViewProps = Pick<
  MainViewComponentProps<AnalysisMainViewItemType.FLOWCHART>,
  'analysis'
>;

/**
 * A flowchart representation of an analysis, as rendered full-screen in the analysis tool.
 */
export default function AnalysisFlowchartView({analysis}: AnalysisFlowchartViewProps): JSX.Element {
  const [stepIdToEdit, setStepIdToEdit] = useState<string | null>(null);
  const stepToEdit = analysis.steps.find((step) => step.id === stepIdToEdit);

  const canModifyStep = (stepId: string): CanEditReturnValue => {
    const step = analysis.steps.find((step) => step.id === stepId);
    assertAndContinue(step != null, 'Could not find step');
    if (step == null) {
      return {canEdit: false, message: 'This step has been deleted.'};
    }

    // Currently, editing transform/filter steps is the only non-experimental interactive flowchart feature.
    // Prevent editing any other step types.
    if (
      !BenchlingConfig.ENABLE_ANALYSIS_TOOL_INTERACTIVE_FLOWCHART_EXPERIMENTAL &&
      !stepIsConfigEditorSupported(step)
    ) {
      return {
        canEdit: false,
        message: `${STEP_TYPE_TO_USER_FACING_NAME[step.stepType]} steps cannot be edited from the flowchart.`,
      };
    }

    return checkCanModifyStep(analysis, stepId, 'step', false);
  };

  return (
    <div className={css(styles.wrapper)}>
      <div
        className={css(styles.flowchart)}
        // `ReactFlow` is greedy for space, so must be given an explicit width to not crowd out the sidebar
        style={{width: stepToEdit == null ? '100%' : `calc(100% - ${RightSidebarWidth.WIDE}px)`}}
      >
        <AnalysisFlowchart
          analysis={analysis}
          mode={AnalysisFlowchartMode.INTERACTIVE}
          availableActions={getAvailableActions(setStepIdToEdit, analysis)}
          canModifyStep={canModifyStep}
          canModifyAnalysis={() => checkIsAnalysisArchived(analysis) || {canEdit: true, message: null}}
          refresh={() => refreshAnalysis(analysis.id)}
        />
      </div>
      {stepToEdit && (
        <EditStepRightSidebar
          pipeline={analysis}
          step={stepToEdit}
          onCancel={() => {
            setStepIdToEdit(null);
          }}
          onSave={() => {
            setStepIdToEdit(null);
            refreshAnalysis(analysis.id);
          }}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
  },
  flowchart: {
    height: '100%',
  },
});
